<!-- src/components/comparisons/BudgetWeekPayloadContent.vue -->

<template>
  <div class="core-toolbar">
    <button class="btn btn-small" @click="goPrev">
      <i class="material-icons left"> chevron_left </i>

      Prev period
    </button>

    <div class="toolbar-center">
      <div class="toolbar-title">
        Period {{ period.periodNo }} (Q{{ period.quarterNo }})
      </div>

      <div class="core-helper">
        {{ formatDateDMY(period.periodStartDate) }}
        →
        {{ formatDateDMY(period.periodEndDate) }}
      </div>
    </div>

    <button class="btn btn-small" @click="goNext">
      Next period

      <i class="material-icons right"> chevron_right </i>
    </button>
  </div>

  <div class="core-divider" style="margin: 12px 0"></div>

  <p v-if="loading" class="grey-text">Loading budget…</p>

  <p v-else-if="error" class="red-text">
    {{ error }}
  </p>

  <div v-else class="weeks">
    <div
      v-for="w in weekBlocks"
      :key="w.weekEnding"
      class="core-disclosure week-card"
      :class="{
        'core-disclosure--open': isOpen(w.weekEnding),
      }"
    >
      <div
        class="core-disclosure-header week-header"
        role="button"
        tabindex="0"
        @click.stop="toggle(w.weekEnding)"
        @keydown.enter.stop.prevent="toggle(w.weekEnding)"
        @keydown.space.stop.prevent="toggle(w.weekEnding)"
      >
        <div class="week-grid">
          <div class="cell">
            <div class="label">Week No.</div>

            <div class="value">
              {{ w.weekNo }}
            </div>
          </div>

          <div class="cell">
            <div class="label">Week Ending</div>

            <div class="value">
              {{ formatDateDMY(w.weekEnding) }}
            </div>
          </div>

          <div class="cell right">
            <div class="label">Budget</div>

            <div class="value">
              {{ moneyGBP(w.totalBudget) }}
            </div>
          </div>

          <div class="cell right">
            <div class="label">Actual</div>

            <div class="value">
              {{ moneyGBP(w.totalActual) }}
            </div>
          </div>

          <div class="cell right">
            <div class="label">+/-</div>

            <div
              class="value"
              :class="w.totalDelta < 0 ? 'core-rag--red' : 'core-rag--green'"
            >
              {{ signedMoneyGBP(w.totalDelta) }}
            </div>
          </div>

          <div class="cell right">
            <div class="label">Revised</div>

            <div class="value">
              {{ moneyGBP(w.totalRevised) }}
            </div>
          </div>
        </div>

        <i class="material-icons core-disclosure-chevron chevron" aria-hidden="true">
          {{ isOpen(w.weekEnding) ? "expand_less" : "expand_more" }}
        </i>
      </div>

      <transition name="collapse">
        <div v-show="isOpen(w.weekEnding)" class="core-disclosure-body week-body">
          <div class="week-body-inner">
            <div class="core-table-wrap core-table-wrap--natural">
              <table class="highlight core-table">
                <thead>
                  <tr>
                    <th>Category</th>

                    <th class="right-align">Budget</th>

                    <th class="right-align">Actual</th>

                    <th class="right-align">+/-</th>

                    <th class="right-align">Revised target to hit Budget</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="r in w.lines" :key="w.weekEnding + '|' + r.category">
                    <td>
                      {{ r.category }}
                    </td>

                    <td class="right-align">
                      {{ moneyGBP(r.budget) }}
                    </td>

                    <td class="right-align">
                      {{ moneyGBP(r.actual) }}
                    </td>

                    <td
                      class="right-align"
                      :class="r.delta < 0 ? 'core-rag--red' : 'core-rag--green'"
                    >
                      {{ signedMoneyGBP(r.delta) }}
                    </td>

                    <td class="right-align">
                      {{ moneyGBP(r.revised) }}
                    </td>
                  </tr>

                  <tr class="total-row">
                    <td>
                      <strong> Total </strong>
                    </td>

                    <td class="right-align">
                      <strong>
                        {{ moneyGBP(w.totalBudget) }}
                      </strong>
                    </td>

                    <td class="right-align">
                      <strong>
                        {{ moneyGBP(w.totalActual) }}
                      </strong>
                    </td>

                    <td
                      class="right-align"
                      :class="w.totalDelta < 0 ? 'core-rag--red' : 'core-rag--green'"
                    >
                      <strong>
                        {{ signedMoneyGBP(w.totalDelta) }}
                      </strong>
                    </td>

                    <td class="right-align">
                      <strong>
                        {{ moneyGBP(w.totalRevised) }}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p class="core-helper" style="margin-top: 10px">
              Actuals are sourced from Firestore:
              <code> venues/{venueId}/till_week_summaries/{weekEnding} </code>
              (field
              <code>total_counted</code>). Category drilldown is pro-rated by budget mix.
            </p>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

import { collection, doc, getDoc, getDocs } from "firebase/firestore";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

import { db } from "@/firebase";

import {
  moneyGBP,
  signedMoneyGBP,
  formatDateDMY,
  fiscalContextForWeekEnding,
  weekIndexInFY,
  addDaysISO,
} from "@/scripts/sharedHelpers/formatter";

const CATEGORIES = ["Door", "Liquor", "Food", "Soft", "Other"];

const { venueId, weekEnding } = useVenueContext();

const loading = ref(false);

const error = ref("");

const selectedWeek = ref(null);

const openWeeks = ref(new Set());

const budgetByWeekCat = ref(new Map());

const actualByWeekTotal = ref(new Map());

function isOpen(we) {
  return openWeeks.value.has(we);
}

function toggle(we) {
  const next = new Set(openWeeks.value);

  if (next.has(we)) {
    next.delete(we);
  } else {
    next.add(we);
  }

  openWeeks.value = next;
}

const period = computed(() =>
  selectedWeek.value ? fiscalContextForWeekEnding(selectedWeek.value) : {}
);

function goPrev() {
  if (!period.value?.periodStartDate) {
    return;
  }

  selectedWeek.value = addDaysISO(period.value.periodStartDate, -7);

  load();
}

function goNext() {
  if (!period.value?.periodEndDate) {
    return;
  }

  selectedWeek.value = addDaysISO(period.value.periodEndDate, 7);

  load();
}

const weekBlocks = computed(() => {
  if (!period.value.periodStartDate) {
    return [];
  }

  const out = [];

  const selWeek = selectedWeek.value;

  const selectedIdx = weekIndexInFY(selWeek);

  let vtdTotal = 0;

  for (
    let d = period.value.periodStartDate;
    d <= period.value.periodEndDate;
    d = addDaysISO(d, 7)
  ) {
    const wkIdx = weekIndexInFY(d);

    if (wkIdx > selectedIdx) {
      continue;
    }

    const bTotal = getWeekBudgetTotal(d);

    const aTotal = getWeekActualTotal(d);

    vtdTotal += aTotal - bTotal;
  }

  const remainingWeeks = Math.max(0, period.value.periodEndWeek - selectedIdx);

  for (
    let d = period.value.periodStartDate;
    d <= period.value.periodEndDate;
    d = addDaysISO(d, 7)
  ) {
    const wkIdx = weekIndexInFY(d);

    const totalBudget = getWeekBudgetTotal(d);

    const totalActual = getWeekActualTotal(d);

    const totalDelta = totalActual - totalBudget;

    const revisedSpread = remainingWeeks > 0 ? -vtdTotal / remainingWeeks : 0;

    const computedRevised =
      wkIdx > selectedIdx ? Math.round(totalBudget + revisedSpread) : 0;

    const lines = buildCategoryLines({
      weekEnding: d,

      totalActual,

      totalBudget,

      totalRevised: computedRevised,

      isFuture: wkIdx > selectedIdx,
    });

    out.push({
      weekEnding: d,

      weekNo: wkIdx,

      lines,

      totalBudget,

      totalActual,

      totalDelta,

      totalRevised: wkIdx > selectedIdx ? computedRevised : 0,
    });
  }

  return out;
});

function getWeekBudgetTotal(weekEndingISO) {
  const wk = budgetByWeekCat.value.get(weekEndingISO);

  if (!wk) {
    return 0;
  }

  let s = 0;

  for (const cat of CATEGORIES) {
    s += Number(wk.get(cat) || 0);
  }

  return s;
}

function getWeekActualTotal(weekEndingISO) {
  return Number(actualByWeekTotal.value.get(weekEndingISO) || 0);
}

function buildCategoryLines({ weekEnding, totalActual, totalRevised, isFuture }) {
  const wkBudgetMap =
    budgetByWeekCat.value.get(weekEnding) || new Map(CATEGORIES.map((c) => [c, 0]));

  const weights = CATEGORIES.map((cat) => ({
    cat,

    b: Number(wkBudgetMap.get(cat) || 0),
  }));

  const denom = weights.reduce((s, x) => s + x.b, 0);

  const rawActuals = [];

  const rawRevised = [];

  if (denom > 0) {
    for (const w of weights) {
      const share = w.b / denom;

      rawActuals.push({
        cat: w.cat,

        val: totalActual * share,
      });

      rawRevised.push({
        cat: w.cat,

        val: totalRevised * share,
      });
    }
  } else {
    const share = CATEGORIES.length ? 1 / CATEGORIES.length : 0;

    for (const w of weights) {
      rawActuals.push({
        cat: w.cat,

        val: totalActual * share,
      });

      rawRevised.push({
        cat: w.cat,

        val: totalRevised * share,
      });
    }
  }

  const actualRounded = reconcileRoundedTotals(rawActuals, Math.round(totalActual));

  const revisedRounded = reconcileRoundedTotals(rawRevised, Math.round(totalRevised));

  return CATEGORIES.map((cat) => {
    const b = Number(wkBudgetMap.get(cat) || 0);

    const a = Number(actualRounded.get(cat) || 0);

    const delta = a - b;

    const r = isFuture ? Number(revisedRounded.get(cat) || 0) : 0;

    return {
      category: cat,

      budget: b,

      actual: a,

      delta,

      revised: r,
    };
  });
}

function reconcileRoundedTotals(items, targetTotal) {
  const rounded = new Map();

  const withFrac = [];

  let sum = 0;

  for (const it of items) {
    const base = Math.floor(it.val);

    const frac = it.val - base;

    rounded.set(it.cat, base);

    withFrac.push({
      cat: it.cat,

      frac,
    });

    sum += base;
  }

  let remainder = targetTotal - sum;

  if (remainder > 0) {
    withFrac.sort((a, b) => b.frac - a.frac);

    let i = 0;

    while (remainder > 0 && withFrac.length) {
      const c = withFrac[i % withFrac.length].cat;

      rounded.set(c, Number(rounded.get(c) || 0) + 1);

      remainder--;
      i++;
    }
  } else if (remainder < 0) {
    withFrac.sort((a, b) => a.frac - b.frac);

    let i = 0;

    while (remainder < 0 && withFrac.length) {
      const c = withFrac[i % withFrac.length].cat;

      rounded.set(c, Number(rounded.get(c) || 0) - 1);

      remainder++;
      i++;
    }
  }

  return rounded;
}

async function load() {
  error.value = "";

  const v = venueId.value;

  const we = selectedWeek.value;

  if (!v || !we) {
    return;
  }

  loading.value = true;

  try {
    const fy = fiscalContextForWeekEnding(we);

    const [bMap, aMap] = await Promise.all([
      fetchBudgetByWeekCategoryFS({
        venueId: v,

        date_from: fy.periodStartDate,

        date_to: fy.periodEndDate,
      }),

      fetchActualWeeklyTotalsFS({
        venueId: v,

        date_from: fy.periodStartDate,

        date_to: fy.periodEndDate,
      }),
    ]);

    budgetByWeekCat.value = bMap;

    actualByWeekTotal.value = aMap;

    openWeeks.value = new Set([we]);
  } catch (e) {
    error.value = e?.message || String(e);
  } finally {
    loading.value = false;
  }
}

async function fetchBudgetByWeekCategoryFS({ venueId, date_from, date_to }) {
  const seed = seedWeekCatMap(date_from, date_to);

  const dates = [];

  for (let d = date_from; d <= date_to; d = addDaysISO(d, 7)) {
    dates.push(d);
  }

  const snapshots = await Promise.all(
    dates.map((d) =>
      getDocs(collection(db, "venues", venueId, "budget_weeks", d, "lines"))
    )
  );

  snapshots.forEach((snap, index) => {
    const d = dates[index];

    const wkMap = seed.get(d);

    if (!wkMap) {
      return;
    }

    snap.forEach((docSnap) => {
      const data = docSnap.data() || {};

      const cat = normaliseCategory(data.category || docSnap.id);

      if (!cat) {
        return;
      }

      wkMap.set(cat, Number(wkMap.get(cat) || 0) + Number(data.amount || 0));
    });
  });

  return seed;
}

async function fetchActualWeeklyTotalsFS({ venueId, date_from, date_to }) {
  const weekly = new Map();

  const dates = [];

  for (let d = date_from; d <= date_to; d = addDaysISO(d, 7)) {
    dates.push(d);
  }

  const snapshots = await Promise.all(
    dates.map((d) => getDoc(doc(db, "venues", venueId, "till_week_summaries", d)))
  );

  snapshots.forEach((snap, index) => {
    const d = dates[index];

    const total = snap.exists() ? Number(snap.data()?.total_counted || 0) : 0;

    weekly.set(d, total);
  });

  return weekly;
}

function seedWeekCatMap(date_from, date_to) {
  const m = new Map();

  for (let d = date_from; d <= date_to; d = addDaysISO(d, 7)) {
    m.set(d, new Map(CATEGORIES.map((c) => [c, 0])));
  }

  return m;
}

function normaliseCategory(input) {
  const s = String(input || "")
    .trim()
    .toLowerCase();

  if (!s) {
    return null;
  }

  const map = {
    door: "Door",

    liquor: "Liquor",

    food: "Food",

    soft: "Soft",

    other: "Other",
  };

  if (map[s]) {
    return map[s];
  }

  if (s.includes("soft")) {
    return "Soft";
  }

  return null;
}

watch(
  [venueId, weekEnding],

  () => {
    if (!selectedWeek.value && weekEnding.value) {
      selectedWeek.value = weekEnding.value;
    }

    load();
  },

  {
    immediate: true,
  }
);
</script>

<style scoped>
.toolbar-center {
  flex: 1;
  text-align: center;
}

.toolbar-title {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}

.week-card {
  margin-bottom: 14px;
  border-left: 4px solid rgba(0, 128, 128, 0.25);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

.week-header {
  padding: 0 !important;
  user-select: none;
}

.week-grid {
  display: grid;
  grid-template-columns: 160px 220px 1fr 1fr 1fr 1fr;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding: 18px;
}

.cell .label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
}

.cell .value {
  margin-top: 2px;
  font-size: 18px;
  font-weight: 700;
}

.cell.right {
  text-align: right;
}

.chevron {
  padding-right: 14px;
}

.week-body-inner {
  width: 100%;
}

.total-row td {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.collapse-enter-active,
.collapse-leave-active {
  transition: max-height 220ms ease, opacity 160ms ease;
}

.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

.collapse-enter-to,
.collapse-leave-from {
  max-height: 1400px;
  opacity: 1;
}

@media (max-width: 900px) {
  .week-grid {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .cell.right {
    text-align: left;
  }
}

@media (max-width: 600px) {
  .toolbar-center {
    order: -1;
    text-align: left;
  }

  .toolbar-title {
    font-size: 16px;
  }

  .week-grid {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 14px 12px;
  }

  .cell.right {
    text-align: left;
  }

  .cell .value {
    font-size: 16px;
    overflow-wrap: anywhere;
  }

  .chevron {
    padding-right: 10px;
  }

  .week-body {
    padding: 12px;
  }

  .week-body-inner table {
    min-width: 720px;
  }
}
</style>
